import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, Button, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Modal, { Header } from 'components/Modal';
import { trackEvent } from 'analytics';

import LG from './LG';
import Philips from './Philips';
import Samsung from './Samsung';
import Chromecast from './Chromecast';
import usePsk, { usePin } from './hooks';
import videos from './videos'

const DeviceTypeItem = ({ icon, title, onClick }) => {
  const theme = useTheme();

  return (
    <Stack
      onClick={onClick}
      alignItems="center"
      gap={2}
      sx={{
        cursor: 'pointer',
        border: `${theme.palette.grey[200]} 1px solid`,
        borderRadius: 8,
        padding: 4,
        flexGrow: 1
      }}>
      <Box sx={{ color: 'primary.main', fontSize: 24, fontWeight: 800 }}>
        {icon}
      </Box>
      <Typography component="span" sx={{ fontSize: 13 }}>{title}</Typography>
    </Stack>
  )
}

const DeviceType = ({ onSelect, manufacturer }) => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const theme = useTheme()
  const videoLink = videos[manufacturer]?.[i18n.language]

  const handlePlayVideo = () => {
    trackEvent('stream_cast_config_video_opened', { manufacturer })
    window.open(videoLink, '_blank')
  }

  return (
    <Stack gap={2}>
      <Header icon={<i className="fa-solid fa-gear" />} title={t('stream-cast.actions.config.title')} />
      <Stack sx={{ height: '100%' }} justifyContent="space-between">
        {
          Boolean(videoLink) && (
            <Stack gap={1} mb={2}>
              <Typography component="span" sx={{ fontSize: 13, textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: t('stream-cast.config.device_type.video_disclaimer') }} />
              <Button onClick={handlePlayVideo} size="small" variant="contained" sx={{alignSelf: 'center'}}>
                <i className="fa-solid fa-play" style={{ marginRight: 5, marginTop: 2 }} />
                {t('ui_elements.play_video')}
              </Button>
              <Stack alignItems={'center'} mt={2}>
                <Box sx={{width: '75%', height: '1px', backgroundColor: theme.palette.grey[200]}} />
              </Stack>
            </Stack>
          )
        }
        <Typography component="span" sx={{ fontSize: 13, textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: t('stream-cast.actions.config.device_type.description') }} />
        <Stack direction="row" gap={2} justifyContent="space-around" mt={2}>
          <DeviceTypeItem
            icon={<i className="fa-regular fa-box-arrow-down-arrow-up" style={{ fontSize: 24 }} />}
            title={t('stream-cast.config.device_type.new.title')}
            onClick={() => onSelect('new')}
          />
          <DeviceTypeItem
            icon={<i className="fa-regular fa-box-taped" style={{ fontSize: 24 }} />}
            title={t('stream-cast.actions.device_type.old.title')}
            onClick={() => onSelect('old')}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}
const Home = ({ onCancel, onConfirm }) => {
  const { t } = useTranslation();

  return (
    <Stack gap={2}>
      <Header icon={<i className="fa-solid fa-gear" />} title={t('stream-cast.actions.config.title')} />
      <Stack sx={{ height: '100%' }} justifyContent="space-between">
        <Typography component="span" sx={{ fontSize: 13, textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: t('stream-cast.actions.config.description') }} />
        <Stack direction="row" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={onCancel} size="small">
            {t('stream-cast.actions.resume_configuration_later')}
          </Button>
          <Button variant="contained" color="primary" onClick={onConfirm} size="small">
            {t('stream-cast.actions.resume_configuration_now')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

export const ConfigChromecast = ({device}) => {
  const navigate = useNavigate();
  return (
    <Modal onClose={() => navigate('..')} maxHeight="70vh">
      <Stack sx={{ position: 'relative', overflow: 'auto' }}>
        <Chromecast device={device} />
      </Stack>
    </Modal>
  )
}

const Config = ({ device }) => {
  const [step, setStep] = useState('home');
  const [deviceType, setDeviceType] = useState(null);
  const navigate = useNavigate();
  const { psk } = usePsk({ device })
  const { pin } = usePin()
  if (!device) {
    return null
  }
  const manufacturer = device.manufacturer?.toLowerCase()
  if (!manufacturer) {
    return null
  }
  return (
    <Modal onClose={() => navigate('..')}>
      <Stack sx={{ position: 'relative' }}>
        {step === 'home' && <Home onCancel={() => navigate('..')} onConfirm={() => setStep('device-type')} />}
        {step === 'device-type' && <DeviceType
          manufacturer={manufacturer}
          onSelect={(type) => {
            trackEvent('stream_cast_config_started', { manufacturer, device_type: type })
            setDeviceType(type)
            setStep('config')
          }}
        />}
        {
          step === 'config' && manufacturer === 'lg' && <LG device={device} deviceType={deviceType} psk={psk} pin={pin} />
        }
        {
          step === 'config' && manufacturer === 'philips' && <Philips device={device} deviceType={deviceType} psk={psk} pin={pin} />
        }
        {
          step === 'config' && manufacturer === 'samsung' && <Samsung device={device} deviceType={deviceType} psk={psk} pin={pin} />
        }
      </Stack>
    </Modal>
  )
}



export default Config
